import { bearerAuth, headerAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import logo from "./logo.svg";

const KEYWORDS =
  "dato content management delivery cma cda records models item types uploads assets graphql real-time preview";

export const datoCms = defineProduct({
  name: "DatoCMS",
  tagLine: "Headless CMS",
  logo,
  tags: ["documents", "engineering"],
  variants: [
    productVariant({
      identifier: "datoCms",
      authLabel: "Content Management API",
      keywords: KEYWORDS,
      protocol: "http",
      defaultUrlPattern: () => "https://site-api\\.datocms\\.com/.*",
      auth: (app) =>
        headerAuth([
          {
            name: "Authorization",
            value: app.secret.apiToken,
            scheme: "Bearer",
          },
          { name: "Accept", value: "application/json" },
          { name: "X-Api-Version", value: "3" },
        ]),
      steps: [
        {
          title: "Get a Content Management API token",
          items: [
            {
              kind: "markdown",
              content:
                "In your [DatoCMS project](https://dashboard.datocms.com/), open **Settings → API tokens**. Use the project’s built-in read-only token for read-only workflows, or create a custom token with **Content Management API** access and a role granting only the actions your workflows need.",
            },
          ],
        },
        {
          title: "Paste your API token",
          items: [
            {
              kind: "input",
              label: "Content Management API token",
              required: true,
              target: { kind: "secrets", field: "apiToken" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: () => "https://site-api.datocms.com/site",
      },
      usageHint: () =>
        "Use the JSON:API Content Management API at https://site-api.datocms.com. The proxy injects the bearer token, Accept, and X-Api-Version: 3, so send a placeholder Authorization header. For requests with a body, send Content-Type: application/vnd.api+json. Resource attributes are nested under data.attributes.",
    }),
    productVariant({
      identifier: "datoCmsContentDelivery",
      authLabel: "Content Delivery API",
      keywords: KEYWORDS,
      protocol: "http",
      defaultUrlPattern: () =>
        "https://(graphql|graphql-listen)\\.datocms\\.com/.*",
      auth: (app) => bearerAuth(app.secret.apiToken),
      steps: [
        {
          title: "Get a Content Delivery API token",
          items: [
            {
              kind: "markdown",
              content:
                "In your [DatoCMS project](https://dashboard.datocms.com/), open **Settings → API tokens**. Use the project’s read-only token, or create a custom token with **Content Delivery API** access. Enable preview access only if your workflows need drafts or real-time preview updates.",
            },
          ],
        },
        {
          title: "Paste your API token",
          items: [
            {
              kind: "input",
              label: "Content Delivery API token",
              required: true,
              target: { kind: "secrets", field: "apiToken" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: () => ({
          url: "https://graphql.datocms.com/",
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ query: "{ _site { locales } }" }),
        }),
      },
      usageHint: () =>
        "POST GraphQL queries to https://graphql.datocms.com/. Use https://graphql-listen.datocms.com for real-time updates. The proxy injects the bearer token, so send a placeholder Authorization header. Send Content-Type: application/json. To query drafts, send X-Include-Drafts: true with a token that has preview access.",
    }),
  ],
});
